/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-16 22:11:48
 *
 *******************************************************/
/// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, FieldArray, getFormValues, change } from 'redux-form';
import { FormLayout, Stack, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import ValuesList from 'src/components/views/filter-tree/item/option-modal/general-tab/price/values-list';
import { isRequired, hasTag, isDelimiter } from 'src/helpers/form-validation-helper';
import { normalizeNumber, normalizeNumberWithComma } from 'src/helpers/form-validation-helper';

import { FILTER_OPTION_DISPLAY_TYPES_PRICE } from 'src/constants/filter-tree';

const isRequiredValue = isRequired();

type Props = {
  priceDisplayType: string,
  priceList: string,
  themeSetupV2: boolean
};

type ComposedProps = Props & WithI18nProps;

class Price extends Component<ComposedProps> {
  render() {
    const { priceDisplayType, priceList, updatePriceListItem, themeSetupV2, i18n } = this.props;
    const displayTypes = FILTER_OPTION_DISPLAY_TYPES_PRICE.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });

    return (
      <FormLayout>
        <Field
          name="priceDisplayType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Label'
          )}
          options={displayTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Tooltip'
          )}
        />
        {priceDisplayType === 'range' ? (
          <React.Fragment>
            <Stack distribution="fill">
              <Field
                name="priceSliderRange"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SliderRange_Label'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SliderRange_Tooltip'
                )}
                validate={[isRequiredValue, hasTag]}
                normalize={(value, prevValue) => normalizeNumber(value, prevValue)}
              />
              <Field
                name="priceSliderStep"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SliderStep_Label'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SliderStep_Tooltip'
                )}
                validate={[isRequiredValue, hasTag]}
                normalize={(value, prevValue) => normalizeNumberWithComma(value, prevValue)}
              />
              <Field
                name="priceSliderDelimiter"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_Delimiter_Label'
                )}
                validate={[isDelimiter]}
                tooltip={i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_Delimiter_Tooltip'
                )}
              />
            </Stack>
            {themeSetupV2 && (
              <div style={{ marginTop: '1.25rem' }}>
                <Field
                  name={`priceHideTextBoxes`}
                  component={RenderToggleSwitch}
                  label={i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_HideValueTextBoxes_Label'
                  )}
                />
              </div>
            )}

            {themeSetupV2 && (
              <div style={{ marginTop: '1.25rem' }}>
                <Field
                  name="priceShortenPipsRange"
                  component={RenderToggleSwitch}
                  label={i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field_ShortenPipsRange_Label'
                  )}
                  tooltip={
                    <span>
                      {i18n.translate(
                        'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field_ShortenPipsRange_Tooltip',
                        {
                          t1: (
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field_ShortenPipsRange_Tooltip_t1'
                              )}
                            </TextStyle>
                          ),
                          t2: (
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field_ShortenPipsRange_Tooltip_t2'
                              )}
                            </TextStyle>
                          ),
                          t3: (
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field_ShortenPipsRange_Tooltip_t3'
                              )}
                            </TextStyle>
                          ),
                          t4: (
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field_ShortenPipsRange_Tooltip_t4'
                              )}
                            </TextStyle>
                          )
                        }
                      )}
                    </span>
                  }
                />
              </div>
            )}
          </React.Fragment>
        ) : (
          <div className="price-values">
            <RenderLabelTooltip
              label={i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_RenderLabelTooltip_Label'
              )}
              tooltip={i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_RenderLabelTooltip_Tooltip'
              )}
            />
            <Field
              name="priceExcludePriceFromValue"
              component={RenderCheckbox}
              label={i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field6_Label'
              )}
              tooltip={i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Price_Field6_Tooltip'
              )}
            />
            <FieldArray
              name="priceList"
              component={ValuesList}
              priceList={priceList}
              updatePriceListItem={updatePriceListItem}
            />
          </div>
        )}
      </FormLayout>
    );
  }
}

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);

  return {
    priceDisplayType: _.get(formFilterOptionValues, 'priceDisplayType', ''),
    priceList: _.get(formFilterOptionValues, 'priceList', ''),
    themeSetupV2: _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false)
  };
};

const mapDispatchToProps = dispatch => ({
  updatePriceListItem: (index, data) =>
    dispatch(change(`OptionModalForm`, `priceList[${index}]`, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(Price));
